import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import Slide from '@mui/material/Slide';

const COOKIE_CONSENT_KEY = 'zeta_cookie_consent';

type CookieConsentStatus = 'accepted' | 'rejected' | null;

// Extend Window interface to include Google Analytics gtag function
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    _env_?: {
      ELASTIC_RUM_enabled?: boolean;
      ELASTIC_RUM_active?: boolean;
      ELASTIC_RUM_serviceName?: string;
      ELASTIC_RUM_serviceVersion?: string;
      ELASTIC_RUM_environment?: string;
      ELASTIC_RUM_breakdownMetrics?: boolean;
      ELASTIC_RUM_serverUrl?: string;
      ELASTIC_RUM_serverUrlPrefix?: string;
      GA?: string;
    };
  }
}

export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY) as CookieConsentStatus;
    if (!consent) {
      // Small delay to ensure smooth page load
      const timer = setTimeout(() => setShowBanner(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    setShowBanner(false);

    // Initialize Google Analytics if it hasn't been initialized yet
    if (window.gtag && window._env_?.GA) {
      // Update consent to granted
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
      });

      // Load Google Analytics script if not already loaded
      const existingScript = document.querySelector('script[src*="googletagmanager.com/gtag/js"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${window._env_.GA}`;
        script.onload = () => {
          if (window.gtag && window._env_?.GA) {
            window.gtag('config', window._env_.GA);
          }
        };
        document.head.appendChild(script);
      } else {
        // Script already exists, just update config
        if (window.gtag && window._env_?.GA) {
          window.gtag('config', window._env_.GA);
        }
      }
    }
  };

  const handleReject = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'rejected');
    setShowBanner(false);

    // Update Google Analytics consent to denied
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
      });
    }
  };

  return (
    <Slide direction="up" in={showBanner} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          px: { xs: 2, sm: 3, lg: 4 },
          py: 2,
        }}
      >
        <Paper
          elevation={8}
          sx={{
            maxWidth: 1560,
            mx: 'auto',
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 2, sm: 3 }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Cookie Preferences
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We use cookies to enhance your experience, analyze site usage, and assist in our marketing efforts. By
                clicking "Accept", you consent to our use of cookies.{' '}
                <Link
                  href="https://zetawealth.co.uk/privacy#cookies"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontWeight: 500 }}
                >
                  Learn more
                </Link>
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="outlined"
                size="medium"
                onClick={handleReject}
                sx={{ flex: { xs: 1, sm: 'none' }, minWidth: 80 }}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                size="medium"
                onClick={handleAccept}
                sx={{ flex: { xs: 1, sm: 'none' }, minWidth: 80 }}
              >
                Accept
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    </Slide>
  );
}

export default CookieConsent;
